import { Link } from "react-router-dom";
import { ArrowRight, Compass, HardHat, Hammer, ScrollText } from "lucide-react";
import { COMPANY } from "@/data/site";

const VALUES = [
  { icon: HardHat, t: "Trade-led", d: "Run by people who came up on the tools — not from a boardroom. Decisions are practical, not theoretical." },
  { icon: Hammer,  t: "Quality first", d: "We’d rather take an extra day than hand over a job we wouldn’t put on our own house." },
  { icon: Compass, t: "Straight talk", d: "Plain English quotes, honest timelines and a phone that gets answered." },
  { icon: ScrollText, t: "Compliance", d: "Insurance, RAMS, retrofit coordination — boring documents, done properly, every time." },
];

const About = () => (
  <div data-testid="about-page">
    {/* HERO */}
    <section className="bg-crest-green text-white">
      <div className="mx-auto max-w-7xl px-6 py-20 md:py-28 grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <p className="text-xs uppercase tracking-[0.22em] text-crest-gold">About Cresttor</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]">
            New name above the door.<br/>
            <span className="text-crest-gold">Decades</span> of trade behind it.
          </h1>
        </div>
        <div className="lg:col-span-4 text-white/80 font-body leading-relaxed">
          Cresttor Construction Ltd is the next chapter for a North West crew that has been quietly building, rendering and renovating across Liverpool, Manchester, Cheshire and beyond since the 1990s.
        </div>
      </div>
    </section>

    {/* STORY */}
    <section className="mx-auto max-w-6xl px-6 py-20 md:py-28">
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <p className="text-xs uppercase tracking-[0.22em] text-crest-gold font-medium">Our story</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-crest-navy leading-tight">Built on what came before.</h2>
          <hr className="gold-rule mt-6" />
        </div>
        <div className="md:col-span-8 space-y-5 text-muted-foreground font-body leading-relaxed text-base">
          <p>
            Cresttor Construction Ltd is a brand-new construction company — registered in England &amp; Wales and based in Liverpool. But the people inside the business are anything but new.
          </p>
          <p>
            Our directors, project managers and lead trades have spent more than three decades on construction sites across the North West. Between them, the team has delivered thousands of square metres of external wall insulation, hundreds of domestic extensions and renovations, fully-managed commercial fit-outs and landscape transformations from Wirral to Wales.
          </p>
          <p>
            Cresttor exists to bring that experience under one cleaner, more focused banner: a tighter team, a sharper scope of services and a way of working that puts the homeowner or commercial client first from day one.
          </p>
          <p>
            We are <span className="text-crest-navy font-medium">a new company</span> — but the steady hands, the long-running supply relationships and the reputation for showing up when we say we will are all carried forward.
          </p>
        </div>
      </div>
    </section>

    {/* AIMS */}
    <section className="bg-crest-surface border-y border-border">
      <div className="mx-auto max-w-7xl px-6 py-20 md:py-28">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="text-xs uppercase tracking-[0.22em] text-crest-gold font-medium">Our aims</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-crest-navy leading-tight">
              What Cresttor is here to do.
            </h2>
          </div>
          <div className="md:col-span-8 grid sm:grid-cols-2 gap-5">
            {VALUES.map((v) => (
              <div key={v.t} className="rounded-lg border border-border bg-white p-7 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg">
                <v.icon className="h-7 w-7 text-crest-green" />
                <h3 className="mt-5 font-display text-lg font-semibold text-crest-navy">{v.t}</h3>
                <p className="mt-2 text-sm text-muted-foreground font-body leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* EXPERTISE */}
    <section className="mx-auto max-w-7xl px-6 py-20 md:py-28 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-5">
        <img src={COMPANY.brandedVan}
             alt="Cresttor Construction signage and branded vehicle"
             className="rounded-lg border border-border w-full"
             data-testid="about-collateral" />
      </div>
      <div className="lg:col-span-7">
        <p className="text-xs uppercase tracking-[0.22em] text-crest-gold font-medium">Expertise</p>
        <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold text-crest-navy leading-tight">
          Render, retrofit, build, landscape.
        </h2>
        <p className="mt-5 text-muted-foreground font-body max-w-2xl">
          Our work clusters around four specialisms — chosen because they are exactly what our crew is best at. We don’t take on projects that fall outside what we can deliver brilliantly.
        </p>
        <ul className="mt-8 grid sm:grid-cols-2 gap-3 text-sm font-body">
          {[
            "External Wall Insulation (EWI)",
            "Silicone, mineral & acrylic renders",
            "PAS 2035 retrofit coordination",
            "Funded scheme experience (ECO/GBIS)",
            "Single & two-storey extensions",
            "Loft conversions & dormers",
            "Commercial fit-outs & refurbs",
            "Driveways, patios & garden walls",
          ].map((c) => (
            <li key={c} className="flex items-center gap-3 rounded-md border border-border bg-white px-4 py-3">
              <span className="h-1.5 w-1.5 rounded-full bg-crest-gold" />
              <span className="text-crest-navy">{c}</span>
            </li>
          ))}
        </ul>
        <Link to="/services" className="mt-8 inline-flex items-center gap-2 rounded-md bg-crest-green px-7 py-3.5 text-sm font-semibold text-white hover:bg-crest-green-700 transition-colors duration-200" data-testid="about-services-cta">
          Browse our services <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>

    {/* AREA */}
    <section className="bg-crest-navy text-white">
      <div className="mx-auto max-w-7xl px-6 py-16 md:py-20 grid md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-7">
          <p className="text-xs uppercase tracking-[0.22em] text-crest-gold">Where we work</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold">
            Liverpool · Greater Manchester · Cheshire · Lancashire · North Wales · Midlands
          </h2>
          <p className="mt-4 text-white/75 font-body max-w-2xl">
            Based at our Liverpool yard, the Cresttor team works actively across the North West and surrounding regions. If you’re unsure whether we cover your area, just give us a call — we usually do.
          </p>
        </div>
        <div className="md:col-span-5 flex md:justify-end">
          <Link to="/contact" className="inline-flex items-center gap-2 rounded-md border border-crest-gold/50 px-7 py-3.5 text-sm font-semibold text-crest-gold hover:bg-crest-gold hover:text-crest-navy transition-colors duration-200" data-testid="about-contact-cta">
            Get in touch <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  </div>
);

export default About;
